import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, avatarUrl, API } from "@/lib/api";
import { Btn, Card, Label, Input, Textarea, StatusBadge } from "@/components/shared";
import { DocumentManager } from "@/components/DocumentManager";
import { MergeClientsDialog } from "@/components/MergeClientsDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Trash2, Mail, Phone, MapPin, Eye, EyeOff, Search, FolderOpen, Briefcase, FileText, Globe, Upload, FileSpreadsheet, KeyRound, UserCog, GitMerge } from "lucide-react";

const blank = { name: "", company: "", email: "", phone: "", address: "", website: "", bio: "", picture_file_id: null, portal_username: "", portal_password: "", notes: "" };
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [q, setQ] = useState("");
  const [profile, setProfile] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const uploadPic = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingPic(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd);
      setEdit((x) => ({ ...x, picture_file_id: up.data.id }));
      toast.success("Photo set");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingPic(false); }
  };

  const load = useCallback(() => api.get("/client-directory").then((r) => setClients(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setShowPw(false); setEdit({ ...blank }); };
  const openEdit = (c) => { setShowPw(false); setEdit({ ...blank, ...c }); };
  const sendReset = async (c) => {
    if (!window.confirm(`Email a password reset link to ${c.email}?`)) return;
    try { const { data } = await api.post(`/admin/users/${c.user_id}/send-reset`); toast.success(`Reset link sent to ${data.email} (valid 60 min)`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const convertToContractor = async (c) => {
    if (!window.confirm(`Convert ${c.name} from Client to Contractor? They'll be notified by email and moved to your Crew list.`)) return;
    try { await api.post(`/admin/users/${c.user_id}/convert-role`, { to_role: "contractor", notify: true }); toast.success(`${c.name} is now a Contractor`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    if (!edit.name?.trim()) { toast.error("Client name is required"); return; }
    try {
      if (edit.id) await api.put(`/client-directory/${edit.id}`, edit);
      else await api.post("/client-directory", edit);
      toast.success("Client saved"); setEdit(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/client-directory/${confirmDel.id}`); toast.success("Client deleted"); setConfirmDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = clients.filter((c) => !q || `${c.name} ${c.company} ${c.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Directory</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">Clients</h1>
        </div>
        <div className="flex gap-2">
          <Btn variant="secondary" data-testid="merge-logins-btn" onClick={() => setMergeOpen(true)}><GitMerge size={16} /> Merge Logins</Btn>
          <Btn variant="secondary" data-testid="export-directory-btn" onClick={() => window.open(`${API}/export/directory.xlsx?auth=${encodeURIComponent(localStorage.getItem("ss_token") || "")}`, "_blank")}><FileSpreadsheet size={16} /> Export Excel</Btn>
          <Btn data-testid="add-client-btn" onClick={openNew}><Plus size={16} /> Add Client</Btn>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <Input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email…" className="pl-9" />
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-zinc-600 font-mono"><Building2 className="inline mr-2" size={16} />No clients yet. Add the companies that send you work orders.</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c, i) => (
            <Card key={c.id} data-testid={`client-${c.id}`} className="p-5 animate-rise" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-center gap-3">
                {avatarUrl(c) ? (
                  <img src={avatarUrl(c)} alt="" className="w-11 h-11 object-cover border-2 border-black shadow-[2px_2px_0px_#000] shrink-0" />
                ) : (
                  <div className="w-11 h-11 bg-zinc-800 border-2 border-black shadow-[2px_2px_0px_#000] flex items-center justify-center font-head font-black text-[#FF5722] shrink-0">{c.name?.[0]?.toUpperCase() || "?"}</div>
                )}
                <div className="min-w-0">
                  <div className="font-head font-bold text-white truncate">{c.name}</div>
                  {c.company && <div className="font-mono text-[11px] text-[#FF5722] uppercase tracking-wide truncate">{c.company}</div>}
                </div>
              </div>
              <div className="mt-3 space-y-1 text-[12px] text-zinc-400">
                {c.email && <div className="flex items-center gap-1.5 truncate"><Mail size={12} className="text-zinc-600 shrink-0" /> {c.email}</div>}
                {c.phone && <div className="flex items-center gap-1.5"><Phone size={12} className="text-zinc-600 shrink-0" /> {c.phone}</div>}
                {c.website && <div className="flex items-center gap-1.5 truncate"><Globe size={12} className="text-zinc-600 shrink-0" /> {c.website}</div>}
                {c.address && <div className="flex items-start gap-1.5"><MapPin size={12} className="text-zinc-600 mt-0.5 shrink-0" /> {c.address}</div>}
              </div>
              {(c.portal_username || c.portal_password) && (
                <div className="mt-3 pt-2 border-t border-zinc-800 font-mono text-[11px] text-zinc-500">
                  <div className="uppercase tracking-widest text-[9px] text-zinc-600 mb-0.5">Portal login</div>
                  <div className="truncate">{c.portal_username || "—"} / ••••••••</div>
                </div>
              )}
              <div className="mt-4 pt-3 border-t border-zinc-800 flex gap-2">
                <Btn data-testid={`view-client-${c.id}`} onClick={() => setProfile(c)} className="flex-1"><FolderOpen size={14} /> Profile</Btn>
                <Btn variant="secondary" data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)}><Pencil size={14} /></Btn>
                {c.user_id && <Btn variant="secondary" data-testid={`reset-client-${c.id}`} onClick={() => sendReset(c)} title="Email a password reset link"><KeyRound size={14} /></Btn>}
                {c.user_id && <Btn variant="secondary" data-testid={`convert-client-${c.id}`} onClick={() => convertToContractor(c)} title="Convert to Contractor"><UserCog size={14} /></Btn>}
                <Btn variant="danger" data-testid={`delete-client-${c.id}`} onClick={() => setConfirmDel(c)}><Trash2 size={14} /></Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit modal */}
      <MergeClientsDialog open={mergeOpen} onClose={() => setMergeOpen(false)} onDone={load} />
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head font-black uppercase">{edit?.id ? "Edit Client" : "Add Client"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact Name *</Label><Input data-testid="client-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
                <div><Label>Company</Label><Input data-testid="client-company" value={edit.company} onChange={(e) => setEdit({ ...edit, company: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input data-testid="client-email" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input data-testid="client-phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
              </div>
              <div><Label>Address</Label><Input data-testid="client-address" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} /></div>
              <div><Label>Website</Label><Input data-testid="client-website" value={edit.website || ""} onChange={(e) => setEdit({ ...edit, website: e.target.value })} placeholder="https://…" /></div>
              <div><Label>Bio</Label><Textarea data-testid="client-bio" value={edit.bio || ""} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} placeholder="Short description of this client / company…" /></div>
              <div>
                <Label>Profile Photo</Label>
                <div className="flex items-center gap-3">
                  {(edit.picture_file_id || edit.picture) ? (
                    <img src={avatarUrl(edit)} alt="" className="w-14 h-14 object-cover border-2 border-black shadow-[2px_2px_0px_#000]" />
                  ) : (
                    <div className="w-14 h-14 bg-zinc-800 border-2 border-black flex items-center justify-center font-head font-black text-[#FF5722]">{edit.name?.[0]?.toUpperCase() || "?"}</div>
                  )}
                  <label className="inline-flex">
                    <input type="file" accept="image/*" className="hidden" data-testid="client-pic-input" onChange={uploadPic} disabled={uploadingPic} />
                    <span className={`inline-flex items-center gap-2 font-head font-bold uppercase text-xs px-3 py-2 border-2 border-zinc-700 bg-zinc-900 text-white cursor-pointer hover:border-[#FF5722] shadow-[2px_2px_0px_#000] ${uploadingPic ? "opacity-50 pointer-events-none" : ""}`}><Upload size={13} /> {uploadingPic ? "Uploading…" : "Upload"}</span>
                  </label>
                </div>
              </div>
              <div className="border border-zinc-800 p-3 bg-black/30 space-y-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722]">Client portal login (admin only)</div>
                <div><Label>Username</Label><Input data-testid="client-username" value={edit.portal_username} onChange={(e) => setEdit({ ...edit, portal_username: e.target.value })} /></div>
                <div>
                  <Label>Password</Label>
                  <div className="relative">
                    <Input data-testid="client-password" type={showPw ? "text" : "password"} value={edit.portal_password} onChange={(e) => setEdit({ ...edit, portal_password: e.target.value })} className="pr-10" />
                    <button type="button" data-testid="toggle-client-pw" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#FF5722]">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  </div>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea data-testid="client-notes" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
              <Btn data-testid="save-client-btn" onClick={save} className="w-full">Save Client</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client profile */}
      <ClientProfile client={profile} onClose={() => setProfile(null)} navigate={navigate} />

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-[#141417] border-2 border-red-600 text-white rounded-none">
          <DialogHeader><DialogTitle className="font-head font-black uppercase text-red-400">Delete client?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-300">Are you sure you want to delete <span className="text-white font-semibold">{confirmDel?.name}</span>? This cannot be undone.</p>
          <div className="flex gap-2 justify-end mt-2">
            <Btn variant="secondary" onClick={() => setConfirmDel(null)}>Cancel</Btn>
            <Btn variant="danger" data-testid="confirm-delete-client" onClick={doDelete}>Yes, delete</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientProfile({ client, onClose, navigate }) {
  const [data, setData] = useState({ jobs: [], bids: [] });
  useEffect(() => {
    if (client?.id) api.get(`/client-directory/${client.id}/records`).then((r) => setData(r.data)).catch(() => {});
  }, [client]);
  if (!client) return null;
  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head font-black uppercase flex items-center gap-2">
            {avatarUrl(client) ? <img src={avatarUrl(client)} alt="" className="w-8 h-8 object-cover border-2 border-black" /> : <FolderOpen size={18} className="text-[#FF5722]" />}
            {client.company || client.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="font-mono text-xs text-zinc-400 space-y-0.5">
            <div>{client.name}{client.company ? ` · ${client.company}` : ""}</div>
            {client.email && <div className="flex items-center gap-1.5"><Mail size={11} /> {client.email}</div>}
            {client.phone && <div className="flex items-center gap-1.5"><Phone size={11} /> {client.phone}</div>}
            {client.website && <div className="flex items-center gap-1.5"><Globe size={11} /> {client.website}</div>}
            {client.bio && <p className="text-zinc-300 mt-2 whitespace-pre-wrap normal-case font-sans text-sm leading-relaxed">{client.bio}</p>}
          </div>

          <div data-testid="client-work-orders">
            <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5 mb-2"><Briefcase size={15} className="text-[#FF5722]" /> Work Orders ({data.jobs.length})</div>
            {data.jobs.length === 0 ? <div className="text-zinc-600 font-mono text-xs border-2 border-dashed border-zinc-900 p-3 text-center">No work orders linked to this client yet.</div> : (
              <div className="space-y-2">
                {data.jobs.map((j) => (
                  <button key={j.id} data-testid={`profile-job-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)} className="w-full text-left border border-zinc-800 bg-black/30 p-2.5 hover:border-[#FF5722] transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white text-sm truncate">{j.address}</span>
                      <StatusBadge status={j.archived ? "archived" : j.status} />
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{j.service_type} · {j.assigned_crew_name || "Unassigned"}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div data-testid="client-bids">
            <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5 mb-2"><FileText size={15} className="text-[#FF5722]" /> Saved Bids ({data.bids.length})</div>
            {data.bids.length === 0 ? <div className="text-zinc-600 font-mono text-xs border-2 border-dashed border-zinc-900 p-3 text-center">No bids saved to this client yet. Link a bid from the Bids page.</div> : (
              <div className="space-y-2">
                {data.bids.map((b) => (
                  <button key={b.id} data-testid={`profile-bid-${b.id}`} onClick={() => navigate(`/bids?open=${b.id}`)} className="w-full text-left border border-zinc-800 bg-black/30 p-2.5 hover:border-[#FF5722] transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white text-sm truncate">{b.title}</span>
                      <span className="font-head font-black text-[#FF5722]">{money(b.totals?.total)}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5"><StatusBadge status={b.status} /><span className="font-mono text-[10px] text-zinc-500">{b.line_items?.length || 0} line items</span></div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <DocumentManager basePath={`/client-directory/${client.id}/documents`} title="Documents" hint="Insurance, price lists, contact info, contracts or bids — shared on this client's profile." testid="client-doc-manager" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
